/**
 * @fileoverview Does schema injection
 *
 * @author Chris Williams (cwilliams@yext.com)
 *
 * We don't use the yext namespace because it clashes with the externs for
 * the analytics library, which is loaded dynamically.
 */
goog.provide('yextschema.schema');

goog.require('goog.array');
goog.require('goog.dom');
goog.require('goog.events');
goog.require('goog.json');
goog.require('goog.net.jsloader');
goog.require('goog.object');
goog.require('goog.html.TrustedResourceUrl');
goog.require('goog.string');
goog.require('yextschema.Analytics');
goog.require('yextschema.helpers');
goog.require('yextschema.NoOpAnalytics');

/**
 * Reference to analytics object used for custom analytics
 *
 * @type {!yextschema.Analytics}
 */
var analytics;

/**
 * Main function for schema injection
 *
 * Name starts with _ to imply that it shouldn't be used by non-Yext users
 *
 * @param {!Object} data
 */
yextschema.schema._embed = function(data) {
  var userData = {
    'entities': goog.object.unsafeClone(data['entities']),
  };

  var injectDataInDom = function() {
    // If analytics script is not loaded, create a dummy analytics object that will do nothing.
    // (this allows supporting "no analytics" mode without making major changes)
    if (!data['noAnalytics']) {
      analytics = new yextschema.Analytics(data);
    } else {
      analytics = new yextschema.NoOpAnalytics();
    }

    var entities = data['entities'];
    var requireEntityId = entities.length !== 1;
    goog.array.forEach(entities, function(entity) {
      yextschema.schema.embedEntity_(entity, requireEntityId, data['directions-text']);
    });
  };

  // Check if the page is loaded.
  if (document.readyState === 'loading') {
    // Run our stuff after the page loads.
    goog.events.listen(
      document,
      goog.events.EventType.DOMCONTENTLOADED,
      injectDataInDom,
    );
  } else {
    // Page is already loaded. Run our stuff.
    injectDataInDom();
  }

  // Clients can supply a callback function as a query parameter.
  // That function is called here.
  var callbackName = data['callback'];
  if (callbackName) {
    var callbackFn = window[callbackName];

    // Display a friendly error message with the function name despite Closure
    // Compiler renaming variables.
    if (!(typeof callbackFn === 'function')) {
      throw new TypeError(callbackName + ' is not a function');
    }

    callbackFn(userData);
  }
};

/**
 * Implementation of the Yext.customEvent function
 *
 * @param {string} eventName
 * @param {string} entityId
 */
yextschema.schema.customEvent = function(eventName, entityId) {
  analytics.event(eventName, entityId);
};

/**
 * @private
 * @param {!Object} entity
 * @param {boolean} requireEntityId
 * @param {string} directionsText
 */
yextschema.schema.embedEntity_ = function(entity, requireEntityId, directionsText) {
  var entityId = entity['entityId'];
  var entityType = entity['entityType'];

  var linkHooks = {
    'phone': function() {
      analytics.event('phonecall', entityId);
    },
    'alternatePhone': function() {
      analytics.event('phonecall', entityId);
    },
    'tollFreePhone': function() {
      analytics.event('phonecall', entityId);
    },
    'mobilePhone': function() {
      analytics.event('phonecall', entityId);
    },
    'faxPhone': function() {
      analytics.event('phonecall', entityId);
    },
    'ttyPhone': function() {
      analytics.event('phonecall', entityId);
    },
    'get-directions-link': function() {
      analytics.event('drivingdirection', entityId);
    },
  };

  // fill data-yext-field elements
  var attributes = entity['attributes'];
  yextschema.schema.injectTag_(
    'div',
    attributes,
    yextschema.schema.setDivValue_,
    entityId,
    requireEntityId,
  );
  yextschema.schema.injectTag_(
    'ul',
    attributes,
    yextschema.schema.setListValue_,
    entityId,
    requireEntityId,
  );
  yextschema.schema.injectTag_(
    'ol',
    attributes,
    yextschema.schema.setListValue_,
    entityId,
    requireEntityId,
  );
  yextschema.schema.injectTag_(
    'img',
    attributes,
    yextschema.helpers.injectImg,
    entityId,
    requireEntityId,
  );
  yextschema.schema.injectTag_(
    'span',
    attributes,
    yextschema.schema.setSpanValue_,
    entityId,
    requireEntityId,
  );
  yextschema.schema.injectTag_(
    'a',
    attributes,
    function(element, link) {
      var attribute = element.getAttribute('data-yext-field');
      if (linkHooks[attribute]) {
        goog.events.listen(
          element,
          goog.events.EventType.CLICK,
          linkHooks[attribute],
        );
      }

      element.href = link;

      if (attribute === 'get-directions-link') {
        if (goog.string.isEmptyOrWhitespace(goog.dom.getTextContent(element))) {
          goog.dom.setTextContent(element, directionsText);
        }
        if (goog.string.isEmptyOrWhitespace(link)) {
          goog.dom.setTextContent(element, '');
        }
      }

      if (attribute === 'email') {
        if (goog.string.isEmptyOrWhitespace(goog.dom.getTextContent(element))) {
          goog.dom.setTextContent(
            element,
            link,
          );
        }
        element.href = 'mailto:' + link;
      }

      if ( attribute === 'phone'
            || attribute === 'localPhone'
            || attribute === 'alternatePhone'
            || attribute === 'tollFreePhone'
            || attribute === 'mobilePhone'
            || attribute === 'faxPhone'
            || attribute === 'ttyPhone'
      ) {
        if (goog.string.isEmptyOrWhitespace(goog.dom.getTextContent(element))) {
          goog.dom.setTextContent(
            element,
            link,
          );
        }
        element.href = 'tel:' + link;
      }
    },
    entityId,
    requireEntityId,
  );

  // add schema json script tag if schema exists
  if (entity['schema']) {
    goog.dom.appendChild(
      document.body,
      goog.dom.createDom(
        'script',
        {
          'type': 'application/ld+json',
          'class': 'yext-schema-json',
          'data-yext-id': entityId,
        },
        goog.json.serialize(entity['schema']),
      ),
    );
  }
};

/**
 * @private
 * @param {string} tagName
 * @param {!Object} values
 * @param {?Function} injectMethod
 * @param {string} entityId
 * @param {boolean} requireEntityId
 */
yextschema.schema.injectTag_ = function(
  tagName,
  values,
  injectMethod,
  entityId,
  requireEntityId,
) {
  // If values is empty or undefined - do nothing (this can happen when an entity doesn't have
  // any tags of a certain type; for instance, events don't currently have any div-s).
  if (!values) {
    return;
  }

  var inject = function(element) {
    if (yextschema.helpers.skipElement(element, entityId, requireEntityId)) {
      return;
    }

    var attr = element.getAttribute('data-yext-field');
    var value = values[attr];
    if (value !== undefined) {
      injectMethod(element, value);
    }
  };

  goog.array.forEach(goog.object.clone(document.getElementsByTagName(tagName)), inject);
};

/**
 * @private
 * @param {!Element} element
 * @param {!Array|string|number} value
 */
yextschema.schema.setSpanValue_ = function(element, value) {
  // If the value is an array, output as a comma separated list
  if (Array.isArray(value)) {
    goog.dom.setTextContent(element, value.join(', '));
    return;
  }

  // The value is not an array.
  goog.dom.setTextContent(element, value);
};

/**
 * @private
 * @param {!Element} element
 * @param {!Array|string|number} value
 */
yextschema.schema.setDivValue_ = function(element, value) {
  if (!Array.isArray(value)) {
    goog.dom.setTextContent(element, value);
    return;
  }

  var children;
  var attribute = element.getAttribute('data-yext-field');

  // The value is an array, wrap each item in its own div.
  if (attribute === 'hours') {
    children = value.map(function(val) {
      // Basic error checking and resort to default
      if (typeof val !== 'string' || val.indexOf('day ') === -1) {
        return goog.dom.createDom('div', {}, val);
      }

      // The index of the space between days and hours
      // eg. for 'Tuesday - Saturday 9:00 am - 5:30 pm', splitIndex = 18
      //     for 'Monday Closed', splitIndex = 6
      var splitIndex = val.lastIndexOf('day ') + 3;
      var daysSpan = goog.dom.createDom(
        'span',
        {'class': 'days'},
        val.substring(0, splitIndex));
      var hoursSpan = goog.dom.createDom(
        'span',
        {'class': 'hours-open'},
        val.substring(splitIndex));
      return goog.dom.createDom('div', {}, daysSpan, hoursSpan);
    });
  } else {
    children = value.map(function(val) {
      return goog.dom.createDom('div', {}, val);
    });
  }

  goog.dom.removeChildren(element);
  goog.dom.append(element, children);
};

/**
 * @private
 * @param {?Element} element
 * @param {!Array|string|number} value
 */
yextschema.schema.setListValue_ = function(element, value) {
  // UL tags can only contain arrays
  if (Array.isArray(value)) {
    var children = value.map(function(val) {
      return goog.dom.createDom('li', {}, val);
    });
    goog.dom.append(element, children);
    return;
  }
};

goog.exportSymbol('Yext._embed', yextschema.schema._embed);
goog.exportSymbol('Yext.customEvent', yextschema.schema.customEvent);
